// ===================================
// ENHANCED API SERVICE FOR PHASE 3 INTEGRATION
// src/services/api.ts
// ===================================
import axios, { AxiosError, AxiosResponse } from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";
// Add session management utilities
const SESSION_STORAGE_KEY = "slavo_chat_session_id";
const AGENTIC_AUTH_KEY = "agentic_verified_at";
const AGENTIC_PRINCIPAL_KEY = "agentic_principal_id";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Session management functions
export const sessionManager = {
  getSessionId: (): string => {
    let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  },

  clearSession: (): void => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(AGENTIC_AUTH_KEY);
    localStorage.removeItem(AGENTIC_PRINCIPAL_KEY);
  },

  isAgenticVerified: (): boolean => {
    const verifiedAt = localStorage.getItem(AGENTIC_AUTH_KEY);
    if (!verifiedAt) return false;

    const verificationTime = parseInt(verifiedAt);
    const now = Date.now();
    const ttl = 24 * 60 * 60 * 1000; // 24 hours

    return now - verificationTime < ttl;
  },

  setAgenticVerified: (principalId?: string): void => {
    localStorage.setItem(AGENTIC_AUTH_KEY, Date.now().toString());
    if (principalId) {
      localStorage.setItem(AGENTIC_PRINCIPAL_KEY, principalId);
    }
  },

  getPrincipalId: (): string | null => {
    return localStorage.getItem(AGENTIC_PRINCIPAL_KEY);
  },
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add session ID for consistent principal identification
    const sessionId = sessionManager.getSessionId();
    config.headers["x-session-id"] = sessionId;
    config.headers["x-public-session-id"] = sessionId;

    if (process.env.NODE_ENV === "development") {
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ENHANCE the response interceptor to handle agentic auth better:
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (process.env.NODE_ENV === "development") {
    }
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken");
      // Only redirect if we're in the app context, not during API calls
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    // Handle agentic auth required
    const data: any = error.response?.data;
    if (
      error.response?.status === 403 &&
      (data?.error === "AGENTIC_AUTH_REQUIRED" ||
        data?.error === "agentic_auth_required")
    ) {
      // Let the calling component handle this - don't redirect
    }

    return Promise.reject(error);
  }
);

// ===================================
// PUBLIC CHAT API (NO AUTH REQUIRED)
// ===================================
export const publicChatApi = {
  // Create new public chat session
  createSession: (
    data: {
      visitor_info?: {
        name?: string;
        email?: string;
        company?: string;
        page_url?: string;
        referrer?: string;
      };
      initial_message?: string;
    } = {}
  ) => axios.post(`${API_BASE_URL}/public-chat/sessions`, data),

  // Get public session with messages
  getSession: (
    sessionId: string,
    params: {
      message_limit?: number;
    } = {}
  ) =>
    axios.get(`${API_BASE_URL}/public-chat/sessions/${sessionId}`, { params }),
  authenticateSession: (
    sessionId: string,
    data: {
      accessCode: string;
    }
  ) =>
    axios.post(`${API_BASE_URL}/public-chat/sessions/${sessionId}/auth`, data),

  // Send message in public chat
  sendMessage: (
    sessionId: string,
    data: {
      message: string;
      visitor_info?: object;
    }
  ) =>
    axios.post(
      `${API_BASE_URL}/public-chat/sessions/${sessionId}/messages`,
      data
    ),

  // Get public chat health
  health: () => axios.get(`${API_BASE_URL}/public-chat/health`),

  // Get company information
  getCompanyInfo: () => axios.get(`${API_BASE_URL}/public-chat/company-info`),
};

// ===================================

// ===================================
// ENHANCED CHAT API
// ===================================
export const chatApi = {
  // Create new chat session
  createSession: (data: {
    title?: string;
    type?: "general" | "document_qa" | "rag_chat";
    context_documents?: string[];
  }) => api.post("/chat/sessions", data),

  // Get user's chat sessions
  getSessions: (
    params: {
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get("/chat/sessions", { params }),

  // Get specific session with messages
  getSession: (
    sessionId: string,
    params: {
      message_limit?: number;
    } = {}
  ) => api.get(`/chat/sessions/${sessionId}`, { params }),

  // Update session details
  updateSession: (
    sessionId: string,
    data: {
      title?: string;
      status?: "active" | "archived";
      context_documents?: string[];
    }
  ) => api.put(`/chat/sessions/${sessionId}`, data),

  // Delete session
  deleteSession: (sessionId: string) =>
    api.delete(`/chat/sessions/${sessionId}`),

  // Send message with RAG support
  sendMessage: (
    sessionId: string,
    data: {
      message: string;
      use_rag?: boolean;
      search_options?: {
        limit?: number;
        similarity_threshold?: number;
        search_type?: "semantic" | "keyword" | "hybrid";
        document_ids?: string[];
        file_types?: string[];
      };
      temperature?: number;
      include_citations?: boolean;
    }
  ) => api.post(`/chat/sessions/${sessionId}/messages`, data),

  // Regenerate last message
  regenerateMessage: (
    sessionId: string,
    data: {
      use_rag?: boolean;
      temperature?: number;
    }
  ) => api.post(`/chat/sessions/${sessionId}/regenerate`, data),

  // Get chat analytics
  getAnalytics: () => api.get("/chat/analytics"),
};

// ===================================
// ENHANCED SEARCH API
// ===================================
export const searchApi = {
  // Get search statistics
  stats: () => api.get("/search/stats"),

  // Similarity search
  similar: (data: {
    query: string;
    limit?: number;
    similarity_threshold?: number;
    search_type?: "semantic" | "keyword" | "hybrid";
    document_ids?: string[];
    file_types?: string[];
  }) => api.post("/search/similar", data),

  // RAG search with comprehensive responses
  rag: (data: {
    query: string;
    conversation_history?: any[];
    search_options?: {
      limit?: number;
      similarity_threshold?: number;
      search_type?: "semantic" | "keyword" | "hybrid";
      document_ids?: string[];
      file_types?: string[];
    };
    temperature?: number;
    max_tokens?: number;
    include_citations?: boolean;
    confidence_threshold?: number;
  }) => api.post("/search/rag", data),

  // Find similar documents
  similarDocuments: (
    documentId: string,
    params: {
      limit?: number;
      similarity_threshold?: number;
    } = {}
  ) => api.get(`/search/documents/${documentId}/similar`, { params }),

  // Search health check
  health: () => api.get("/search/health"),

  // Test search system
  test: (data = {}) => api.post("/search/test", data),
};

// ===================================
// ENHANCED DOCUMENT API
// ===================================
export const documentApi = {
  // Upload document
  upload: (formData: FormData) =>
    api.post("/documents/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // List documents with filtering
  list: (
    params: {
      page?: number;
      limit?: number;
      file_type?: string;
      processing_status?: string;
      extraction_status?: string;
      embedding_status?: string;
      search_term?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
      date_range?: {
        start: string;
        end: string;
      };
      access_level?: string;
    } = {}
  ) => api.get("/documents/", { params }),

  // Get document details
  get: (id: string) => api.get(`/documents/${id}`),

  // Download document
  download: (id: string) =>
    api.get(`/documents/${id}/download`, { responseType: "blob" }),

  // Get document content with chunks
  content: (id: string) => api.get(`/documents/${id}/content`),

  // Reprocess document
  reprocess: (id: string) => api.post(`/documents/${id}/reprocess`),

  // Delete document
  delete: (id: string) => api.delete(`/documents/${id}`),

  // Get document analytics
  analytics: (id: string) => api.get(`/documents/${id}/analytics`),
};

// ===================================
// EMBEDDINGS API
// ===================================
export const embeddingsApi = {
  // Generate embeddings for document
  generate: (
    documentId: string,
    data: {
      force_regenerate?: boolean;
      batch_size?: number;
    } = {}
  ) => api.post(`/search/embeddings/generate/${documentId}`, data),

  // Get embedding status
  status: (
    params: {
      document_ids?: string[];
      business_id?: number;
    } = {}
  ) => api.get("/search/embeddings/status", { params }),
};

// ===================================
// ADMIN API
// ===================================
export const adminApi = {
  // Dashboard analytics
  dashboard: (
    params: {
      business_id?: number;
    } = {}
  ) => api.get("/admin/dashboard", { params }),

  // Document management
  documents: (
    params: {
      page?: number;
      limit?: number;
      business_id?: number;
      user_id?: string;
      status?: string;
      file_type?: string;
      search?: string;
    } = {}
  ) => api.get("/admin/documents", { params }),

  // Processing jobs
  processingJobs: (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      job_type?: string;
      business_id?: number;
    } = {}
  ) => api.get("/admin/processing-jobs", { params }),

  // Trigger job processing
  processJobs: () => api.post("/admin/processing-jobs/process"),

  // System maintenance
  cleanupJobs: (data: { days_old?: number } = {}) =>
    api.post("/admin/maintenance/cleanup-jobs", data),

  // System status
  systemStatus: () => api.get("/admin/system/status"),
};

// ===================================
// ANALYTICS API
// ===================================
export const analyticsApi = {
  // Overall analytics
  overview: (
    params: {
      business_id?: number;
      days?: number;
    } = {}
  ) => api.get("/analytics/overview", { params }),

  // Search analytics
  search: (
    params: {
      business_id?: number;
      days?: number;
    } = {}
  ) => api.get("/analytics/search", { params }),

  // RAG analytics
  rag: (
    params: {
      business_id?: number;
      days?: number;
    } = {}
  ) => api.get("/analytics/rag", { params }),

  // User activity
  userActivity: (
    params: {
      business_id?: number;
      days?: number;
    } = {}
  ) => api.get("/analytics/user-activity", { params }),

  // Document analytics
  documents: (
    params: {
      business_id?: number;
      days?: number;
    } = {}
  ) => api.get("/analytics/documents", { params }),
};

// ===================================
// HEALTH CHECK API
// ===================================
export const healthApi = {
  // System health
  check: () => api.get("/health"),

  // Search health
  searchHealth: () => searchApi.health(),

  // Test endpoints
  test: () => api.get("/test"),

  testProcessing: () => api.get("/test/processing"),

  testSearch: () => api.get("/test/search"),
};

// ===================================
// AUTH API (if separate from main api)
// ===================================
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    api.post("/auth/login", credentials),

  logout: () => api.post("/auth/logout"),

  me: () => api.get("/auth/me"),

  refresh: () => api.post("/auth/refresh"),
};

// ===================================
// AGENTIC AUTH API (Phase C)
// ===================================
export const agenticAuthApi = {
  // Verify access key or credentials with enhanced response handling
  verify: async (data: {
    accessKey?: string;
    username?: string;
    password?: string;
  }) => {
    try {
      const response = await api.post("/agentic/verify", data);

      // Store verification state on success
      if (response.data.ok) {
        sessionManager.setAgenticVerified(response.data.principalId);
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  },

  // Check current verification status
  status: () => api.get("/agentic/status"),

  // Debug endpoint for development
  debug: () => {
    if (process.env.NODE_ENV === "development") {
      return api.get("/agentic/debug");
    }
    return Promise.reject(
      new Error("Debug endpoint only available in development")
    );
  },
};
// ADD these new chat API helpers:
export const chatHelpers = {
  // Retry a message that was blocked by agentic auth
  retryBlockedMessage: async (sessionId: string, messageData: any) => {
    return chatApi.sendMessage(sessionId, messageData);
  },

  // Check if a message requires agentic auth
  requiresAgenticAuth: (error: any): boolean => {
    return (
      error.response?.status === 403 &&
      (error.response?.data?.error === "AGENTIC_AUTH_REQUIRED" ||
        error.response?.data?.error === "agentic_auth_required")
    );
  },
};

// ===================================
// INTENT DETECTION API
// ===================================
export const intentDetectionApi = {
  detect: (data: { message: string }) => api.post("/intent/detect", data),
};

export default api;
